import { Inject, Injectable, forwardRef } from '@kaokei/use-vue-service';
import { changeToken } from '@core/api/login';
import { getSignInUserInfo } from '@core/api/user';
import type { UserVO } from '@core/types/user';
import { DURATION29, EXCLUDE_PAGES } from '@core/utils/constant';
import { PERM_ROOT_AUTH } from '../utils/permission';
import { AdtagService } from './adtag.service';
import { BindphoneService } from './bindphone.service';
import { LevelService } from './level.service';
import { LoginService } from './login.service';
import { MessageService } from './message.service';
import { ProfileService } from './profile.service';
import { RouterService } from './router.service';
import { StorageService } from './storage.service';
import { ZoneService } from './zone.service';

@Injectable()
export class UserService {
  // UserService代表内存中的用户数据，刷新页面就会丢失
  // 所以还需要把相关用户数据存储到localStorage中，刷新页面需要从localStorage中获取
  // 目前只有token和user，其他数据尽量转移到自己的服务中进行管理
  public token = '';
  public user = {} as UserVO;

  @Inject(forwardRef(() => ProfileService))
  public profileService!: ProfileService;

  @Inject(forwardRef(() => BindphoneService))
  public bindphoneService!: BindphoneService;

  @Inject(forwardRef(() => LoginService))
  public loginService!: LoginService;

  @Inject(forwardRef(() => RouterService))
  public routerService!: RouterService;

  @Inject(forwardRef(() => StorageService))
  public storageService!: StorageService;

  @Inject(forwardRef(() => AdtagService))
  public adtagService!: AdtagService;

  @Inject(forwardRef(() => ZoneService))
  public zoneService!: ZoneService;

  @Inject(forwardRef(() => MessageService))
  public messageService!: MessageService;

  @Inject(forwardRef(() => LevelService))
  public levelService!: LevelService;

  /**
   * 页面加载时从本地存储中初始化用户数据
   * 如果本地存储没有用户数据，路由中间件可能会强制登录
   */
  public initUserWhenPageLoaded(query: any) {
    // 只在页面加载时持久化adtag
    this.adtagService.setAdtag(query.adtag);
    const token = this.storageService.getUserToken();
    const user = this.storageService.getUserInfo();
    if (token && user) {
      // 先临时使用本地数据，防止用户长时间等待空白页面
      this.token = token;
      this.user = user;
      // 再通过api获取最新的用户数据
      return this.fetchUserInfoFromRemote()
        .catch(() => {
          // 获取用户数据失败需要清除内存中用户数据
          // 注意这里必须是先catch再then
          this.reset();
        })
        .then(() => {
          // 静默获取用户数据失败，这里直接刷新页面
          // 因为本地数据已经被清除，不会再次进入这个逻辑
          // 会被路由中间件拦截，进入首页或者停留在当前页面
          if (this.isLogin()) {
            const needBindphone = this.needBindphone();
            if (needBindphone) {
              // 优先显示绑定手机号的弹窗
              this.bindphoneService.setVisible(true);
            } else {
              // 然后再显示个人资料弹窗
              this.profileService.setVisible(this.needCompleteProfile());
            }
          } else {
            this.routerService.reload();
          }
        });
    }
  }

  /**
   * 尝试续期token-其实是更换一个新的token
   */
  public tryUpdateToken() {
    if (this.token) {
      const arr = this.token.split('.');
      if (arr.length === 3) {
        const mid = arr[1];
        try {
          const midObj = JSON.parse(window.atob(mid));
          if (midObj.exp) {
            // 过期时间小于29天-这里不是30天是为了避免重复创建新的token
            const expFlag =
              new Date().getTime() + DURATION29 >= midObj.exp * 1000;
            if (expFlag) {
              changeToken().then((res) => this.updateToken(res.token));
            }
          }
        } catch (error) {
          // ignore
        }
      }
    }
  }

  /**
   * 绑定手机号可能导致token更新
   */
  public updateToken(token: string) {
    this.token = token;
    this.storageService.setUserToken(token);
  }

  /**
   * 绑定手机号可能导致用户信息更新
   */
  public updateUser(user: UserVO) {
    this.user = user;
    this.storageService.setUserInfo(user);
    if (user.token) {
      this.updateToken(user.token);
    }
    this.updateAnalysisSdkConfig(user);
  }

  /**
   * 更新用户的鼠标拖尾图片
   */
  private updateUserCursorTrails(img: string) {
    this.user.cursorTrails = img;
    this.storageService.setUserInfo(this.user);
  }
  /**
   * 更新用户的头像框图片
   */
  private updateUserAvatarFrame(img: string) {
    this.user.avatarFrame = img;
    this.storageService.setUserInfo(this.user);
  }
  /**
   * 更新用户的主页背景图片
   */
  private updateUserHomeBackground(img: string) {
    this.user.homeBackground = img;
    this.storageService.setUserInfo(this.user);
  }
  /**
   * 更新用户的成绩表装饰图片
   */
  private updateUserGradeDecoration(img: string) {
    this.user.gradeDecoration = img;
    this.storageService.setUserInfo(this.user);
  }

  /**
   * 更换装备的统一入口
   * @param img 装备对应的图片，如果为空则表示卸下装备
   * @param type 装备类型
   */
  public updateUserEquip(img: string, type: number) {
    switch (type) {
      case 1:
        this.updateUserCursorTrails(img);
        break;
      case 2:
        this.updateUserAvatarFrame(img);
        break;
      case 3:
        this.updateUserGradeDecoration(img);
        break;
      case 4:
        this.updateUserHomeBackground(img);
        break;
      default:
        break;
    }
  }

  /**
   * 登录成功之后获取到token
   * 然后再通过token获取用户数据
   */
  public initUserByToken(token: string, reload = true) {
    this.updateToken(token);
    return this.fetchUserInfoFromRemote()
      .then(() => {
        // 登录成功-刷新当前页面
        // 因为是登录弹窗，所以刷新当前页面
        // 因为当前页面可能是既有登录状态又有非登录状态
        if (this.isLogin()) {
          if (reload) {
            // 飞书登录不能直接刷新页面，因为是飞书的回调页面，会导致死循环
            // 会在业务代码中重定向到首页
            this.loginService.hideLoginModal();
            this.routerService.reloadAfterLogin();
          }
        } else {
          throw new Error('获取用户信息失败');
        }
      })
      .catch((e) => {
        this.reset();
        throw e;
      });
  }

  /**
   * 获取服务器端最新的用户数据
   * 并存储到本地存储中
   */
  public fetchUserInfoFromRemote() {
    const adtag = this.adtagService.adtag;
    const loginSource = this.loginService.loginSource;
    return getSignInUserInfo(loginSource, adtag).then((res) => {
      this.updateUser(res);
      // 注意这里代表用户已经登录成功
      // 包括页面加载时静默登录+用户主动登录两种场景都会调用这个方法
      // this.levelService.init(); 激励相关暂时屏蔽
      // 获取用户信息成功后删除adtag
      this.adtagService.removeAdtag();
      // 尝试更新token
      this.tryUpdateToken();
      // 用户登录成功后再获取消息列表
      this.messageService.init();
    });
  }

  private updateAnalysisSdkConfig(user: UserVO) {
    const userId = user?.userId;
    if (userId) {
      window.analysisSdk.update({ userId });
      window.analysisSdk.saLogin(userId);
    }
  }

  private isRootManager() {
    return this.user.permissions?.includes(PERM_ROOT_AUTH);
  }
  public hasAuthOnly(perm: string | string[]) {
    const permArr = typeof perm === 'string' ? [perm] : perm;
    return permArr.some((p) => this.user.permissions?.includes(p));
  }
  public hasAuth(perm: string | string[]) {
    if (this.isRootManager()) {
      return true;
    }
    const permArr = typeof perm === 'string' ? [perm] : perm;
    return permArr.some((p) => this.user.permissions?.includes(p));
  }
  public hasGroupAuth(perm: string | string[]) {
    if (this.hasAuth(perm)) {
      return true;
    }
    const groupId = this.routerService.getRouteQueryGroupId();
    if (!groupId) {
      return false;
    }
    const curGroup = this.user.myGroup?.find((group) => group.gid === groupId);
    if (!curGroup) {
      return false;
    }
    const permArr = Array.isArray(perm) ? perm : [perm];
    return permArr.some((p) => curGroup.permissions?.includes(p) ?? false);
  }
  public hasGroupAuthOnly(perm: string | string[]) {
    const publicAuth = this.hasAuthOnly(perm);
    if (publicAuth) {
      return true;
    }
    const groupId = this.routerService.getRouteQueryGroupId();
    if (!groupId) {
      return false;
    }
    const curGroup = this.user.myGroup?.find((group) => group.gid === groupId);
    if (!curGroup) {
      return false;
    }
    const permArr = Array.isArray(perm) ? perm : [perm];
    return permArr.some((p) => curGroup.permissions?.includes(p) ?? false);
  }

  public hasNoAuth(perm: string | string[]) {
    return !this.hasAuth(perm);
  }

  public hasNoGroupAuth(perm: string | string[]) {
    return !this.hasGroupAuth(perm);
  }

  public isLogin() {
    return !!this.user.userId;
  }

  public hasAdminPermission() {
    if (this.isAdmin()) {
      return true;
    }
    if (this.user.myGroup) {
      return this.user.myGroup.some((group) => !!group.permissions?.length);
    }
    return false;
  }

  // 只用来控制右上角后台管理的入口展示+后台切换域的选择器
  // 基本只要是有角色的用户isAdmin=true
  // 所以以后增加新的角色，只需要后端注意同时设置isAdmin=true就可以了
  public isAdmin() {
    return this.user.isAdmin;
  }

  public needBindphone() {
    const u = this.user;
    return !u.phoneNumber;
  }

  public needCompleteProfile() {
    const route = this.routerService.getRoute();
    //route.name偶现undefined,但route.meta.name稳定有值，规范上优先取route.name
    const routeName = route.name || route.meta.name;
    const isExcludePage = EXCLUDE_PAGES.includes(routeName);
    if (!routeName || isExcludePage) {
      return false;
    }
    const u = this.user;
    return !u.nickname;
  }

  public reset() {
    this.token = '';
    (this.user as any) = {};
    this.storageService.removeUserToken();
    this.storageService.removeUserInfo();
  }

  // 代理一下LoginService
  // 因为大多数业务只需要UserService而不需要LoginService
  // 从而避免业务代码中需要引入LoginService
  public showLoginModal() {
    if (!this.user.userId) {
      return this.loginService.showLoginModal();
    }
  }

  // 获取准考证号
  public getAdmissionNumber() {
    if (this.user.userId) {
      const ids = `${this.user.userId}`;
      const gap = Math.max(8 - ids.length, 0);
      const zero = '0'.repeat(gap);
      return `H${zero}${ids}`;
    }
  }
}
